/**
 * useRecurringGifts — hook + API client for the donor recurring-gifts surface.
 *
 * Backed by /api/recurring-gifts. All calls send the JWT (email/pass students)
 * or session_token cookie (Google donors) automatically via the shared axios
 * client from AuthContext (which sets `withCredentials`).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";

const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

export const CADENCE_LABELS = {
  weekly: "Every week",
  monthly: "Every month",
  quarterly: "Every three months",
  annual: "Once a year",
};

export const formatDollars = (cents) => {
  const n = (cents || 0) / 100;
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: n % 1 ? 2 : 0 });
};

export const formatWhen = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

export const useGivingSummary = ({ auto = true } = {}) => {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    if (!auto) return;
    client.get("/recurring-gifts/summary").then((res) => setSummary(res.data)).catch(() => setSummary(null));
  }, [auto]);
  return summary;
};

export const useRecurringGifts = () => {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await client.get("/recurring-gifts");
      setGifts(res.data);
    } catch (err) {
      // 401 is fine — the caller will render an anonymous CTA. Don't surface.
      if (err?.response?.status !== 401) {
        setError(err?.response?.data?.detail || "Could not load recurring gifts.");
      }
      setGifts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = useCallback(async (body) => {
    const res = await client.post("/recurring-gifts", body);
    await refresh();
    return res.data;
  }, [refresh]);

  const update = useCallback(async (id, patch) => {
    const res = await client.patch(`/recurring-gifts/${id}`, patch);
    await refresh();
    return res.data;
  }, [refresh]);

  const cancel = useCallback(async (id) => {
    const res = await client.delete(`/recurring-gifts/${id}`);
    await refresh();
    return res.data;
  }, [refresh]);

  return { gifts, loading, error, refresh, create, update, cancel };
};
